/**
 * Opportunity Documents Tab Component
 * Displays and manages documents associated with an opportunity
 */

import React, { useState } from 'react';
import { Card, Button, Badge, LoadingSpinner } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
} from 'lucide-react';

interface OpportunityDocumentsTabProps {
  opportunityId: string;
  projectId: string;
}

interface RFPDocument {
  artifact_id: string;
  filename: string;
  file_size: number;
  uploaded_at: string;
  processing_status: string;
  chunks_count?: number;
  sections_extracted?: number | null;
  entities_count?: number;
}

interface RFPDocumentsListResponse {
  documents: RFPDocument[];
  total: number;
}

interface RFPDocumentUploadResponse {
  artifact_ids: string[];
  job_id: string;
  status: string;
  message: string;
}

export function OpportunityDocumentsTab({
  opportunityId,
  projectId,
}: OpportunityDocumentsTabProps) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const api = getAPIClient();
  const queryClient = useQueryClient();

  // Fetch documents - matches Trace API response format
  const { data: documentsData, isLoading } = useQuery<RFPDocumentsListResponse>({
    queryKey: ['opportunity-documents', opportunityId, projectId],
    queryFn: async () => {
      try {
        // API client's get() method returns response.data directly (not the full axios response)
        // So 'data' here is already the unwrapped response data
        const data = await api.get<RFPDocumentsListResponse>(
          `/api/opportunities/${opportunityId}/rfp-documents`,
          {
            params: { project_id: projectId },
          }
        );
        
        console.log('[DocumentsTab] Fetched documents data:', data);
        
        // Validate structure matches expected format
        if (!data || !data.documents || !Array.isArray(data.documents)) {
          console.warn('[DocumentsTab] Invalid response format, using empty documents');
          return { documents: [], total: 0 };
        }
        return data;
      } catch (error: any) {
        console.error('[DocumentsTab] Failed to fetch documents:', error);
        // Return empty structure on error to prevent React Query undefined error
        return { documents: [], total: 0 };
      }
    },
    enabled: !!opportunityId && !!projectId,
  });

  // Upload mutation - matches Trace API upload response format
  const uploadMutation = useMutation<RFPDocumentUploadResponse, Error, FileList>({
    mutationFn: async (files: FileList) => {
      try {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append('files', file);
        });

        console.log('[DocumentsTab] Uploading files:', Array.from(files).map(f => f.name));
        
        // API client's post() method returns response.data directly (not the full axios response)
        // So 'data' here is already the unwrapped response data
        const data = await api.post<RFPDocumentUploadResponse>(
          `/api/opportunities/${opportunityId}/rfp-documents/upload`,
          formData,
          {
            params: { project_id: projectId },
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        
        console.log('[DocumentsTab] API response data:', data);
        console.log('[DocumentsTab] Response data type:', typeof data);
        
        // Validate response matches Trace API format
        if (!data) {
          console.error('[DocumentsTab] API returned null/undefined response');
          throw new Error('Empty response from upload API');
        }
        // Check for required fields - artifact_ids and job_id are required
        if (!Array.isArray(data.artifact_ids) || data.artifact_ids.length === 0) {
          console.error('[DocumentsTab] Invalid response - missing or empty artifact_ids:', data);
          throw new Error('Invalid upload response - missing artifact_ids');
        }
        if (!data.job_id) {
          console.error('[DocumentsTab] Invalid response - missing job_id:', data);
          throw new Error('Invalid upload response - missing job_id');
        }
        // Success - return the data
        console.log('[DocumentsTab] Upload response validated successfully:', { 
          artifact_ids: data.artifact_ids, 
          job_id: data.job_id, 
          status: data.status,
          message: data.message 
        });
        return data;
      } catch (error: any) {
        console.error('[DocumentsTab] Upload mutation error:', error);
        console.error('[DocumentsTab] Error type:', error.constructor.name);
        console.error('[DocumentsTab] Error message:', error.message);
        console.error('[DocumentsTab] Error response:', error.response);
        // Re-throw to let React Query handle it
        throw error;
      }
    },
    onSuccess: (response: RFPDocumentUploadResponse) => {
      try {
        console.log('[DocumentsTab] Upload successful - onSuccess called:', response);
        // The upload response contains artifact_ids and job_id
        // We need to refetch the documents list to get the full document details
        queryClient.invalidateQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        // Wait a bit for the backend to commit, then refetch multiple times to ensure we get the data
        setTimeout(() => {
          queryClient.refetchQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        }, 300);
        setTimeout(() => {
          queryClient.refetchQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        }, 1000);
        setUploading(false);
        console.log('[DocumentsTab] Upload success handler completed');
      } catch (error: any) {
        console.error('[DocumentsTab] Error in onSuccess handler:', error);
        setUploading(false);
        // Don't show error alert here - upload was successful, just refetch failed
      }
    },
    onError: (error: any) => {
      console.error('[DocumentsTab] Upload failed:', error);
      console.error('[DocumentsTab] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setUploading(false);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error occurred';
      alert(`Upload failed: ${errorMessage}`);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploading(true);
      uploadMutation.mutate(e.target.files);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploading(true);
      // Create a FileList-like object
      const files = e.dataTransfer.files;
      uploadMutation.mutate(files);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="success" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="warning" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
            <Clock className="w-3 h-3 mr-1" />
            Processing
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="default" className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <AlertCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="default">
            {status}
          </Badge>
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">Upload Documents</h3>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-2">
            Drag and drop RFP documents here, or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Supported formats: PDF, DOCX (Max 50MB per file)
          </p>
          <input
            type="file"
            id="file-upload"
            multiple
            accept=".pdf,.docx"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Button
              variant="primary"
              disabled={uploading}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('file-upload')?.click();
              }}
            >
              {uploading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Select Files
                </>
              )}
            </Button>
          </label>
        </div>
      </Card>

      {/* Documents List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Documents ({documentsData?.total || 0})
          </h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : !documentsData || documentsData.documents.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              No documents uploaded yet. Upload RFP documents to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documentsData.documents.map((doc) => (
              <div
                key={doc.artifact_id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-theme-text truncate">
                      {doc.filename}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-theme-text-secondary">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>Uploaded {formatDate(doc.uploaded_at)}</span>
                      {doc.chunks_count !== undefined && (
                        <>
                          <span>•</span>
                          <span>{doc.chunks_count} chunks</span>
                        </>
                      )}
                      {doc.entities_count !== undefined && doc.entities_count > 0 && (
                        <>
                          <span>•</span>
                          <span>{doc.entities_count} entities</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(doc.processing_status)}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      window.open(`/api/artifacts/${doc.artifact_id}/download`, '_blank');
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
